import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Wordmark from '../../components/Brand/Wordmark';

/*
 * The screen an HR account gets when there is no usable tenant behind it — the two states
 * CompanyGate refuses to let into the app.
 *
 *   none       admin provisioning has not attached this account to a company. For the design
 *              partners this is the product's front door.
 *   suspended  the account IS attached, but Velaigal suspended the employer. /api/me reports
 *              that case deliberately (company non-null with status 'suspended', while
 *              `companies` comes back empty) so it does NOT read as never having been
 *              provisioned: the recruiter has a company, it has a name, and nothing they can do
 *              to their own account will change the answer.
 *
 * It renders OUTSIDE the HR Layout on purpose: every sidebar link would 403 with no_company,
 * so offering the chrome would be a menu of dead ends. What it does instead is (a) say plainly
 * why, in the product's own terms, (b) show the exact identity an admin needs to attach — or the
 * company that was suspended — so it can be read down a phone or pasted into an email, and
 * (c) give a real way forward.
 *
 * Design system: cream ground, Fraunces display headline, one 11px tracking-widest meta label
 * per block, hairline rules instead of cards-within-cards, saffron used once (the ordinals),
 * .btn-* utilities for every button, <Wordmark> for the brand. No gradients, no glass blur, no
 * oversized radii, no drop shadow on a default state.
 */

export interface NoCompanyProps {
  /** Which of the two blocking states this is. CompanyGate derives it from companyState(). */
  variant?: 'none' | 'suspended';
}

const NoCompany: React.FC<NoCompanyProps> = ({ variant = 'none' }) => {
  const { user, company, refresh, logout } = useAuth();
  const [checking, setChecking] = useState(false);
  const [checks, setChecks] = useState(0);
  const suspended = variant === 'suspended';

  const handleCheck = async () => {
    if (checking) return;
    setChecking(true);
    try { await refresh(); } finally {
      setChecking(false);
      setChecks((n) => n + 1);
    }
  };

  // If refresh() found a usable company the gate above unmounts this screen, so still being
  // rendered with checks > 0 means the answer was "not yet". The gate is the whole predicate
  // here: the brief's `checks > 0 && !company` would be permanently false on the suspended
  // variant, where `company` is non-null by design and the block is its status, not its absence.
  const stillWaiting = checks > 0;

  return (
    <div className="min-h-screen w-full bg-cream flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-xl">
        <Wordmark variant="lockup" size="md" />

        <span className="label-editorial block mt-12 mb-3">
          {suspended ? 'Employer suspended' : 'Account pending'}
        </span>

        {suspended ? (
          <>
            <h1 className="font-display text-3xl md:text-4xl font-medium text-ink tracking-tight leading-tight">
              {company?.name || 'Your company'} is suspended on Velaigal.
            </h1>
            <p className="mt-5 text-base text-ink-secondary leading-relaxed">
              This is about the company, not about you. Your account is fine and your seat is still
              there, but while the suspension stands the company&apos;s job board is down — its
              published jobs were closed, and no new applications can arrive.
            </p>
          </>
        ) : (
          <>
            <h1 className="font-display text-3xl md:text-4xl font-medium text-ink tracking-tight leading-tight">
              Your account isn&apos;t attached to a company yet.
            </h1>
            <p className="mt-5 text-base text-ink-secondary leading-relaxed">
              On Velaigal a job belongs to a company, not to a person — so the applicants, the tasks
              and the hiring decisions all survive any one recruiter leaving. Until an admin attaches
              your account to yours, there is nothing here to post to.
            </p>
          </>
        )}

        <div className="mt-10 border-t border-hairline pt-6">
          <span className="label-editorial block mb-4">
            {suspended ? 'What this is about' : 'The account to attach'}
          </span>
          <dl className="space-y-3">
            {suspended && (
              <div className="flex gap-4">
                <dt className="text-sm text-ink-tertiary w-20 flex-shrink-0">Company</dt>
                <dd className="text-sm text-ink font-medium break-words">{company?.name || '—'}</dd>
              </div>
            )}
            <div className="flex gap-4">
              <dt className="text-sm text-ink-tertiary w-20 flex-shrink-0">Name</dt>
              <dd className="text-sm text-ink font-medium break-words">{user?.name || '—'}</dd>
            </div>
            <div className="flex gap-4">
              <dt className="text-sm text-ink-tertiary w-20 flex-shrink-0">Email</dt>
              <dd className="text-sm text-ink font-medium break-all">{user?.email || '—'}</dd>
            </div>
            <div className="flex gap-4">
              <dt className="text-sm text-ink-tertiary w-20 flex-shrink-0">Role</dt>
              <dd className="text-sm text-ink font-medium">
                {suspended
                  ? (company?.companyRole === 'owner' ? 'Company owner' : 'Company member')
                  : 'Recruiter'}
              </dd>
            </div>
          </dl>
        </div>

        <div className="mt-8 border-t border-hairline pt-6">
          <span className="label-editorial block mb-4">
            {suspended ? 'What this means' : 'What happens next'}
          </span>
          {suspended ? (
            <ol className="space-y-4">
              <li className="flex gap-3">
                <span className="label-editorial flex-shrink-0 pt-0.5" style={{ color: 'var(--color-saffron)' }}>01</span>
                <p className="text-sm text-ink-secondary leading-relaxed">
                  Nothing was deleted. The jobs, the work candidates submitted against them and
                  every decision already made are all still there.
                </p>
              </li>
              <li className="flex gap-3">
                <span className="label-editorial flex-shrink-0 pt-0.5" style={{ color: 'var(--color-saffron)' }}>02</span>
                <p className="text-sm text-ink-secondary leading-relaxed">
                  Only an admin can lift a suspension, and lifting it does not put the closed jobs
                  back on the board. Reposting them is a deliberate decision you make afterwards.
                </p>
              </li>
              <li className="flex gap-3">
                <span className="label-editorial flex-shrink-0 pt-0.5" style={{ color: 'var(--color-saffron)' }}>03</span>
                <p className="text-sm text-ink-secondary leading-relaxed">
                  If this is unexpected, talk to us and quote the company name above. Being attached
                  to a different company would not help — the suspension is on the employer, not on
                  your account.
                </p>
              </li>
            </ol>
          ) : (
            <ol className="space-y-4">
              <li className="flex gap-3">
                <span className="label-editorial flex-shrink-0 pt-0.5" style={{ color: 'var(--color-saffron)' }}>01</span>
                <p className="text-sm text-ink-secondary leading-relaxed">
                  We create your company record and make you its owner, or add you as a member of
                  one that already exists.
                </p>
              </li>
              <li className="flex gap-3">
                <span className="label-editorial flex-shrink-0 pt-0.5" style={{ color: 'var(--color-saffron)' }}>02</span>
                <p className="text-sm text-ink-secondary leading-relaxed">
                  You fill in the company name, website and what you build. Those three fields are
                  exactly what the AI reads when it drafts the tasks candidates are measured
                  against — nothing else about your company reaches it.
                </p>
              </li>
              <li className="flex gap-3">
                <span className="label-editorial flex-shrink-0 pt-0.5" style={{ color: 'var(--color-saffron)' }}>03</span>
                <p className="text-sm text-ink-secondary leading-relaxed">
                  Post your first job. Nothing you have done so far is lost — this account is
                  already verified and stays as it is.
                </p>
              </li>
            </ol>
          )}
        </div>

        <div className="mt-10 flex items-center gap-3 flex-wrap">
          <button type="button" className="btn-primary" onClick={() => void handleCheck()} disabled={checking}>
            {checking ? (
              <>
                <span
                  aria-hidden="true"
                  className="inline-block h-3.5 w-3.5 rounded-full border-2 animate-spin"
                  style={{ borderColor: 'rgba(244, 240, 232, 0.35)', borderTopColor: 'var(--color-cream)' }}
                />
                <span>Checking…</span>
              </>
            ) : (
              'Check again'
            )}
          </button>
          <Link to="/contact" className="btn-secondary">Contact us</Link>
          <button type="button" className="velaigal-link text-sm font-medium" onClick={() => void logout()}>
            Sign out
          </button>
        </div>

        <p aria-live="polite" className="mt-6 text-xs text-ink-tertiary">
          {suspended
            ? (stillWaiting
              ? 'Still suspended. Lifting a suspension takes effect immediately, so there is nothing to wait for on your side once it is done.'
              : 'Already spoke to someone? Lifting a suspension takes effect immediately — press “Check again”.')
            : (stillWaiting
              ? 'Still nothing attached to this account. Attaching takes effect immediately, so there is nothing to wait for on your side once it is done.'
              : 'Already spoke to someone? Attaching an account takes effect immediately — press “Check again”.')}
        </p>
      </div>
    </div>
  );
};

export default NoCompany;
